import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Grid2,
  IconButton,
  InputAdornment,
  Stack,
  ButtonGroup,
  Menu,
  Button,
  CircularProgress,
  Chip,
  Collapse,
  Divider,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  DescriptionOutlined,
  ArrowDropDown,
  FilterListRounded,
  KeyboardArrowDown,
} from "@mui/icons-material";
import LocationSelect from "../Select/LocationSelect";
import ProgramSelect from "../Select/ProgramSelect";
import SemesterSelect from "../Select/SemesterSelect";

const validationSchema = Yup.object().shape({
  search_term: Yup.string().nullable().optional(),
  location_id: Yup.number().typeError("Must be a number").integer().positive().nullable().optional(),
  program_id: Yup.number().typeError("Must be a number").integer().positive().nullable().optional(),
  date: Yup.date().typeError("Invalid date").nullable().optional(),
  date_end: Yup.date().typeError("Invalid date").nullable().optional(),
  subject_id: Yup.number().nullable().optional(),
  semester_id: Yup.number().nullable().optional(),
  data_type: Yup.string().nullable().optional(),
});

const downloadOptions = [
  { label: "All", value: "all" },
  { label: "Group by students", value: "group_students" },
];

// ─── Compact select field ─────────────────────────────────────────────────────
function FilterSelect({ label, name, value, onChange, children }) {
  const id = `${name}-label`;
  return (
    <FormControl fullWidth size="small">
      <InputLabel id={id}>{label}</InputLabel>
      <Select
        labelId={id}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        label={label}
        sx={{ borderRadius: 1 }}
      >
        {children}
      </Select>
    </FormControl>
  );
}

// ─── Section overline label ───────────────────────────────────────────────────
function FilterLabel({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        fontSize: "0.65rem",
        letterSpacing: "0.1em",
        color: "text.disabled",
        fontWeight: 700,
        display: "block",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

export default function StudentFilters({
  onFilterChange,
  locations,
  programs,
  semesters,
  subjects,
  loading,
  semester_loading,
  requestFile,
  paginationModel
}) {
  const [isSearching] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleClick = async (values) => {
    await requestFile(values, selectedIndex);
  };

  const handleMenuItemClick = (index) => {
    setSelectedIndex(index);
    setAnchorEl(null);
  };

  const handleSearchClick = (values) => {
    let date_parsed = null;
    let date_end_parsed = null;
    if (values.date)
      date_parsed = DateTime.fromJSDate(new Date(values.date)).toSQLDate();
    if (values.date_end)
      date_end_parsed = DateTime.fromJSDate(new Date(values.date_end)).toSQLDate();
    const { page, pageSize } = paginationModel;
    onFilterChange({ ...values, date: date_parsed, date_end: date_end_parsed, page, pageSize});
  };

  const makeHandler = (field, setFieldValue, submitForm, skipSubmit = false) => (e) => {
    try {
      setFieldValue(field, e.target?.value ?? e);
      if (!skipSubmit) submitForm();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Formik
      initialValues={{
        search_term: "",
        location_id: null,
        program_id: null,
        date: null,
        date_end: null,
        subject_id: null,
        semester_id: null,
        data_type: "Sessions",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSearchClick(values)}
    >
      {({ values, handleChange, setFieldValue, submitForm }) => {

        // Count active filters for badge
        const activeCount = [
          values.location_id,
          values.program_id,
          values.subject_id,
          values.semester_id,
          values.date,
          values.date_end,
        ].filter(Boolean).length;

        return (
          <Form>
            <Box sx={{ mb: 1}}>

              {/* ── Top row: search + filter toggle + download ── */}
              <Grid2 container spacing={1} alignItems="flex-start">

                {/* Search */}
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search students…"
                    name="search_term"
                    value={values.search_term}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        submitForm();
                      }
                    }}
                    disabled={isSearching}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {isSearching && <CircularProgress size={16} />}
                          {!isSearching && values.search_term && (
                            <IconButton
                              size="small"
                              onClick={() => setFieldValue("search_term", "")}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid2>

                {/* Filter toggle button */}
                <Grid2 size={{ xs: "auto" }}>
                  <Button
                    size="small"
                    variant={filtersOpen ? "contained" : "outlined"}
                    color={filtersOpen ? "primary" : "inherit"}
                    onClick={() => setFiltersOpen((p) => !p)}
                    startIcon={<FilterListRounded fontSize="small" />}
                    endIcon={
                      activeCount > 0 ? (
                        <Chip
                          size="small"
                          label={activeCount}
                          color="primary"
                          sx={{ height: 18, fontSize: "0.65rem", borderRadius: 1 }}
                        />
                      ) : (
                        <KeyboardArrowDown
                          fontSize="small"
                          sx={{
                            transition: "transform 0.2s",
                            transform: filtersOpen ? "rotate(180deg)" : "none",
                          }}
                        />
                      )
                    }
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      height: 40,
                    }}
                  >
                    Filters
                  </Button>
                </Grid2>

                {/* Spacer */}
                <Grid2 size={{ xs: 12, sm: "grow" }} sx={{ display: { xs: "none", sm: "block" } }} />

                {/* Download split button */}
                <Grid2 size={{ xs: 12, sm: "auto" }}>
                  <ButtonGroup variant="contained" size="small" sx={{ height: 40 }}>
                    <Button
                      onClick={() => handleClick(values)}
                      startIcon={<DescriptionOutlined fontSize="small" />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "4px 0 0 4px",
                        whiteSpace: "nowrap",
                        px: 2,
                      }}
                    >
                      Download — {downloadOptions[selectedIndex].label}
                    </Button>
                    <Button
                      size="small"
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      sx={{ px: 0.5, borderRadius: "0 4px 4px 0" }}
                    >
                      <ArrowDropDown fontSize="small" />
                    </Button>
                  </ButtonGroup>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{ sx: { borderRadius: 1 } }}
                  >
                    {downloadOptions.map((opt, i) => (
                      <MenuItem
                        key={opt.value}
                        selected={i === selectedIndex}
                        onClick={() => handleMenuItemClick(i)}
                        dense
                      >
                        <Typography variant="body2">{opt.label}</Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid2>
              </Grid2>

              {/* ── Collapsible filters panel ── */}
              <Collapse in={filtersOpen}>
                <Box  
                  sx={{
                    mt: 1,
                    p: 2  ,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <Grid2 container spacing={2}>

                    {/* Location */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FilterLabel>Location</FilterLabel>
                      <LocationSelect
                        locations={locations}
                        label="Location"
                        name="location_id"
                        value={values.location_id ?? ""}
                        onChange={(val, e) => {
                          setFieldValue("location_id", e.target.value);
                          submitForm();
                        }}
                      />
                    </Grid2>

                    {/* Program */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FilterLabel>Program</FilterLabel>
                      <ProgramSelect 
                        programs={programs}
                        label="Program"
                        name="program_id"
                        value={values.program_id}
                        onChange={(val, e) => {
                          setFieldValue("program_id", e.target.value);
                          submitForm();
                        }}
                      />
                    </Grid2>

                    {/* Subject */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FilterLabel>Subject</FilterLabel>
                      <FilterSelect
                        label="Subject"
                        name="subject_id"
                        value={values.subject_id}
                        onChange={(e) => {
                          setFieldValue("subject_id", e.target.value);
                          submitForm();
                        }}
                      >
                        <MenuItem value={null}>
                          <Typography variant="body2" color="text.secondary">All subjects</Typography>
                        </MenuItem>
                        {subjects.map((s, i) => (
                          <MenuItem key={i} value={s.id}>
                            <Typography variant="body2">{s.title}</Typography>
                          </MenuItem>
                        ))}
                      </FilterSelect>
                    </Grid2>

                    {/* Semester */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FilterLabel>Semester</FilterLabel>
                      <SemesterSelect 
                        semesters={semesters}
                        label="Semester"
                        name="semester_id"
                        value={values.semester_id}
                        onChange={(val, e) => {
                          setFieldValue("semester_id", e.target.value);
                          submitForm();
                        }}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                      <Divider />
                    </Grid2>

                    {/* Date range */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <FilterLabel>Date range</FilterLabel>
                      <Stack direction="row" spacing={1}>
                        <DatePicker
                          label="From"
                          value={values.date}
                          onChange={(val) => {
                            setFieldValue("date", val);
                            submitForm();
                          }}
                          slotProps={{
                            textField: { size: "small", fullWidth: true, sx: { "& .MuiOutlinedInput-root": { borderRadius: 1 } } },
                          }}
                        />
                        <DatePicker
                          label="To"
                          value={values.date_end}
                          onChange={(val) => {
                            setFieldValue("date_end", val);
                            submitForm();
                          }}
                          slotProps={{
                            textField: { size: "small", fullWidth: true, sx: { "& .MuiOutlinedInput-root": { borderRadius: 1 } } },
                          }}
                        />
                      </Stack>
                    </Grid2>

                    {/* Data type */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FilterLabel>Download data type</FilterLabel>
                      <FilterSelect
                        label="Data type"
                        name="data_type"
                        value={values.data_type}
                        onChange={(e) => setFieldValue("data_type", e.target.value)}
                      >
                        {["Sessions", "Assessments"].map((item) => (
                          <MenuItem key={item} value={item}>
                            <Typography variant="body2">{item}</Typography>
                          </MenuItem>
                        ))}
                      </FilterSelect>
                    </Grid2>

                    {/* Clear filters */}
                    {activeCount > 0 && (
                      <Grid2 size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            setFieldValue("location_id", null);
                            setFieldValue("program_id", null);
                            setFieldValue("subject_id", null);
                            setFieldValue("semester_id", null);
                            setFieldValue("date", null);
                            setFieldValue("date_end", null);
                            submitForm();
                          }}
                          sx={{ textTransform: "none", color: "text.secondary", borderRadius: 1 }}
                        >
                          Clear all filters
                        </Button>
                      </Grid2>
                    )}

                  </Grid2>
                </Box>
              </Collapse>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}